import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ApiService from '../../services/ApiService';
import Loader from '../../components/Loader';
import Paginator from '../../components/Paginator';
import { toast } from 'react-toastify';
import { formatCurrency, formatDate } from '../../utils/helper';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const List = () => {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [categories, setCategories] = useState([]);
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [apiData, setApiData] = useState({
        page: 1,
        search: "",
        start_date: "",
        end_date: "",
        category: ""
    });

    useEffect(() => {
        getCategories();
    }, []);

    useEffect(() => {
        getData();
    }, [apiData.page, apiData.search, apiData.start_date, apiData.end_date, apiData.category]);

    const handleDateChange = (update) => {
        setDateRange(update);
        const [start, end] = update;
        setApiData(prev => ({
            ...prev,
            start_date: start ? formatDate(start, 'YYYY-MM-DD') : "",
            end_date: end ? formatDate(end, 'YYYY-MM-DD') : ""
        }));
    };

    const getCategories = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/expense-categories',
                data: { per_page: 1000 }
            });
            if (response.data.status) {
                setCategories(response.data.data.categories || []);
            }
        } catch (error) {
            console.error('Failed to fetch categories');
        }
    };

    const getData = async () => {
        setIsLoading(true);
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/expenses',
                data: apiData,
            });
            if (response.data.status) {
                const expenses = response.data.data.expenses;
                setData(expenses.data || []);
                setPagination({
                    total: expenses.total,
                    per_page: expenses.per_page,
                    current_page: expenses.current_page,
                    last_page: expenses.last_page,
                });
            }
        } catch (error) {
            toast.error('Failed to fetch expenses');
        } finally {
            setIsLoading(false);
        }
    };

    const deleteItem = async (id) => {
        if (!window.confirm('Are you sure you want to delete this expense?')) return;
        
        try {
            const response = await ApiService.request({
                method: 'DELETE',
                url: `admin/expenses/${id}`
            });
            if (response.data.status) {
                toast.success(response.data.message);
                getData();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to delete expense');
        }
    };

    if (isLoading) return <Loader />;

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Expenses</h1>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <Link to="/expenses/create" className="btn btn-sm fw-bold btn-base">Create Expense</Link>
                    </div>
                </div>
            </div>
            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <div className="d-flex align-items-center position-relative my-1">
                                    <span className="svg-icon svg-icon-1 position-absolute ms-6">
                                        <i className="fas fa-search fs-1"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control form-control-solid w-250px ps-14"
                                        placeholder="Search Expense"
                                        value={apiData.search}
                                        onChange={(e) => setApiData({ ...apiData, search: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="card-toolbar">
                                <div className="d-flex justify-content-end gap-3">
                                    <div className="w-200px">
                                        <select 
                                            className="form-select form-select-solid"
                                            value={apiData.category}
                                            onChange={(e) => setApiData({...apiData, category: e.target.value})}
                                        >
                                            <option value="">All Categories</option>
                                            {categories.map(cat => (
                                                <option key={cat.id} value={cat.name}>{cat.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="w-200px">
                                         <DatePicker
                                            selectsRange={true}
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={handleDateChange}
                                            className="form-control form-control-solid"
                                            placeholderText="Select Date Range"
                                            isClearable={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card-body py-4">
                            <div className="table-responsive">
                                <table className="table align-middle table-row-dashed fs-6 gy-5">
                                    <thead>
                                        <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                            <th>Date</th>
                                            <th>Reference</th>
                                            <th>Category</th>
                                            <th>Amount</th>
                                            <th>Note</th>
                                            <th className="text-end">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-gray-600 fw-semibold">
                                        {data.map((item) => (
                                            <tr key={item.id}>
                                                <td>{formatDate(item.expense_date)}</td>
                                                <td>{item.reference_no}</td>
                                                <td>{item.category}</td>
                                                <td>{formatCurrency(item.amount)}</td>
                                                <td>{item.note}</td>
                                                <td className="text-end">
                                                    <Link to={`/expenses/edit/${item.id}`} className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                                                        <i className="fas fa-edit"></i>
                                                    </Link>
                                                    <button onClick={() => deleteItem(item.id)} className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {data.length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="text-center">No expenses found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <Paginator
                                pagination={pagination}
                                onPageChange={(page) => setApiData((prev) => ({ ...prev, page }))}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;
